import React from 'react';
import {Box, Divider, Stack, Tab, Tabs, Typography} from '@mui/material';
import useDocumentViewerStore from '../../../../stores/useDocumentViewerStore';
import {createReport} from '../../../../firebase/firestore/reports';
import {useSession} from '../../../../firebase/UserProvider';
import {generateUUID} from '../../../../components/prosemirror-editor/helpers/utils';
import {generateLocalId} from '../../../../components/prosemirror-editor/yMaps/sourcePackageYMap';
import DeltaClusterControls from './components/DeltaClusterControls';
import ClaimDeltasView from './components/ClaimDeltasView';
import DeltaStoriesView from './components/DeltaStoriesView';
import DeltaStoryModal from './components/DeltaStoryModal';
import GapsToEeiView from './components/GapsToEeiView';
import {flattenClaimUpdatesToModalStories} from '../../../../utils/api/deltas';
import {extractPlainTextFromContent} from './extractEntitiesFromContent';
import {formatPublishedAt, getDaysSinceDate} from './deltaDateUtils';
import {FINTEL_SEARCH_PARAM_KEYS, getFintelSearchParam} from './deltaSearchParams';
import useDeltaDocumentContent from './hooks/useDeltaDocumentContent';
import useReportDeltaClusters from './hooks/useReportDeltaClusters';
import useDeltaTimeline from './hooks/useDeltaTimeline';
import useDeltaUrlState from './hooks/useDeltaUrlState';
import {bumpDeltaOperationRunId} from '../../../../utils/api/fintelDeltaRunContext';
import {stripReportSaysPrefix} from '../../../../utils/stripReportSaysPrefix';
import {
    DELTA_TAB_TO_MODE,
    DeltaPanelTab,
    deltaModeForActiveTab,
} from '../../../../utils/api/fintelDeltaModes';

const DEFAULT_PRODUCT_TYPE = {value: 'pitch', label: 'Pitch'};
const SOURCE_PACKAGE_DOC_ID = 'sourcePackage';

const formatBaselineForTitle = (deltaBaselineDate) => {
    if (!deltaBaselineDate) return 'Unknown date';
    const parsed = new Date(deltaBaselineDate);
    if (Number.isNaN(parsed.getTime())) return String(deltaBaselineDate);
    return parsed.toISOString().slice(0, 10);
};

const getSegmentIndex = (segment) => {
    if (!segment) return null;
    if (segment.segmentIndex != null) return segment.segmentIndex;
    if (segment.index != null) return segment.index;
    return null;
};

const toNumberOrNull = (value) => {
    if (value == null || value === '') return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};

const parseRangeCandidate = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;
    if (typeof value !== 'string') return null;
    try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'object' && parsed ? parsed : null;
    } catch {
        return null;
    }
};

const normaliseRange = (range) => {
    const parsed = parseRangeCandidate(range);
    if (!parsed) return null;

    const segmentIndex = toNumberOrNull(parsed.segmentIndex);
    const segmentOffset = toNumberOrNull(parsed.segmentOffset);
    if (segmentIndex == null || segmentOffset == null) return null;

    return {segmentIndex, segmentOffset};
};

const buildSegmentBounds = (segment) => {
    const contentLength = typeof segment?.content === 'string' ? segment.content.length : 0;

    const explicitStart = normaliseRange(segment?.start);
    const explicitEnd = normaliseRange(segment?.end);
    if (explicitStart && explicitEnd) {
        return {start: explicitStart, end: explicitEnd};
    }

    const segmentIndex = getSegmentIndex(segment);
    if (segmentIndex == null) {
        return {start: null, end: null};
    }

    const start = explicitStart || {segmentIndex, segmentOffset: 0};
    const fallbackEndOffset = contentLength > 0 ? contentLength : start.segmentOffset;
    const end = explicitEnd || {segmentIndex, segmentOffset: fallbackEndOffset};

    return {start, end};
};

const buildSegmentLocalId = ({segment, start, end}) => {
    const generated = generateLocalId({
        localId: segment?.localId,
        docId: segment?.docId,
        start,
        end,
        index: getSegmentIndex(segment),
        offset: 0,
    });

    if (generated) return generated;

    if (segment?.docId != null && segment?.segmentId != null) {
        return `delta:${segment.docId}:${segment.segmentId}`;
    }

    return `delta:${generateUUID()}`;
};

const buildSourcePackageDocInitFromStories = ({stories, userId}) => {
    const now = Date.now();
    const folders = {};
    const items = {};

    stories.forEach((story, storyIndex) => {
        const folderId = generateUUID();
        folders[folderId] = {
            name: story?.title || `Cluster ${story?.clusterId ?? storyIndex + 1}`,
            parentFolderId: null,
            position: (storyIndex + 1) * 10,
            createdAt: now,
            createdBy: userId ?? null,
        };

        const segments = Array.isArray(story?.segments) ? story.segments : [];
        segments.forEach((segment, segmentIndex) => {
            const itemId = generateUUID();
            const {start, end} = buildSegmentBounds(segment);

            items[itemId] = {
                type: 'snippet',
                parentFolderId: folderId,
                localId: buildSegmentLocalId({segment, start, end}),
                position: (segmentIndex + 1) * 10,
                createdAt: now,
                createdBy: userId ?? null,
                docId: segment?.docId ?? null,
                docTitle: segment?.docTitle ?? null,
                docUrl: segment?.docUrl ?? null,
                docSource: segment?.docSource ?? null,
                docSourceType: segment?.docSourceType ?? null,
                classificationLevel: segment?.classificationLevel ?? segment?.docClassificationLevel ?? null,
                content: segment?.content ?? '',
                originalContent: segment?.content ?? '',
                segmentId: segment?.segmentId ?? null,
                start: start ? JSON.stringify(start) : null,
                end: end ? JSON.stringify(end) : null,
            };
        });
    });

    return {
        [SOURCE_PACKAGE_DOC_ID]: {
            type: 'ymap',
            payload: {folders, items},
        },
    };
};

const buildSourcePackageDocInitFromClaimGroups = ({claimGroups, selectedClaimKeys, userId}) => {
    const selected = (claimGroups || []).filter((g) => selectedClaimKeys.has(String(g.claimKey)));
    const syntheticStories = selected.map((claim) => {
        const seen = new Set();
        const segments = [];
        for (const u of claim.updates || []) {
            for (const seg of u.segments || []) {
                const id = seg?.segmentId;
                if (id == null) continue;
                const sk = String(id);
                if (seen.has(sk)) continue;
                seen.add(sk);
                segments.push(seg);
            }
        }
        const titleBase =
            stripReportSaysPrefix(claim.reportClaim?.trim() || '') || claim.claimId || 'FINTEL claim';
        const title = titleBase.length > 120 ? `${titleBase.slice(0, 117)}…` : titleBase;
        return {title, segments};
    });
    return buildSourcePackageDocInitFromStories({stories: syntheticStories, userId});
};

const FINTEL_SIDE_PANEL_WIDTH = 540;

const DeltaPanel = ({content, reportDoc, isProductLoading = false}) => {
    const {user} = useSession() || {user: null};
    const setDocument = useDocumentViewerStore((s) => s.setDocument);
    const {searchParams, baselineDate, setBaselineDate, updateDeltaSearchParams} = useDeltaUrlState(reportDoc);
    const reportIdForDelta = reportDoc?.id;

    const tabFromUrl = getFintelSearchParam(searchParams, 'TAB');
    const focusGapId = getFintelSearchParam(searchParams, 'GAP');

    const [activeTab, setActiveTab] = React.useState(() => (
        tabFromUrl && DELTA_TAB_TO_MODE[tabFromUrl] != null ? tabFromUrl : DeltaPanelTab.CLAIMS
    ));
    const deltaMode = deltaModeForActiveTab(activeTab);
    const [deltaRunEpoch, setDeltaRunEpoch] = React.useState(0);

    React.useEffect(() => {
        if (tabFromUrl && DELTA_TAB_TO_MODE[tabFromUrl] != null) {
            setActiveTab(tabFromUrl);
        }
    }, [tabFromUrl]);

    const syncTabToUrl = React.useCallback((value) => {
        updateDeltaSearchParams({
            [FINTEL_SEARCH_PARAM_KEYS.TAB]: value === DeltaPanelTab.CLAIMS ? null : value,
            ...(value !== DeltaPanelTab.GAPS ? {[FINTEL_SEARCH_PARAM_KEYS.GAP]: null} : {}),
        });
    }, [updateDeltaSearchParams]);

    const handleTabChange = React.useCallback((_e, value) => {
        syncTabToUrl(value);
        setActiveTab(value);
    }, [syncTabToUrl]);

    const publishedAtForApi = formatPublishedAt(baselineDate);
    const hasPlainReportText = React.useMemo(() => {
        const t = extractPlainTextFromContent(content);
        return Boolean(t && String(t).trim());
    }, [content]);

    const isClaimsTab = activeTab === DeltaPanelTab.CLAIMS;
    const isTimelineTab = activeTab === DeltaPanelTab.TIMELINE;
    const isGapsTab = activeTab === DeltaPanelTab.GAPS;

    const {
        claimGroups,
        clustersLoading: claimsLoading,
        clustersError: claimsError,
        claimDeltaStage,
        claimDeltaMessage,
        claimUpdateProgressPercent,
        segmentClaimBatchIndex,
        segmentClaimBatchTotal,
    } = useReportDeltaClusters({
        content,
        publishedAt: publishedAtForApi,
        reportId: reportIdForDelta,
        deltaMode,
        deltaRunEpoch,
        enabled: isClaimsTab && !isProductLoading && hasPlainReportText && Boolean(publishedAtForApi),
    });

    const {
        stories: timelineStories,
        timelineLoading,
        timelineError,
    } = useDeltaTimeline({
        content,
        publishedAt: publishedAtForApi,
        reportId: reportIdForDelta,
        deltaMode,
        deltaRunEpoch,
        enabled: isTimelineTab && !isProductLoading && hasPlainReportText && Boolean(publishedAtForApi),
    });

    const claimModalStories = React.useMemo(
        () => flattenClaimUpdatesToModalStories(claimGroups),
        [claimGroups]
    );
    const stories = isClaimsTab ? claimModalStories : timelineStories;
    const storiesLoading = isClaimsTab ? claimsLoading : timelineLoading;
    const storiesError = isClaimsTab ? claimsError : timelineError;

    const handleRefreshDeltaRun = React.useCallback(() => {
        const rid = reportIdForDelta;
        if (rid == null || !String(rid).trim()) {
            return;
        }
        bumpDeltaOperationRunId({reportId: rid, deltaMode});
        setDeltaRunEpoch((n) => n + 1);
    }, [reportIdForDelta, deltaMode]);

    const [isStoryModalOpen, setIsStoryModalOpen] = React.useState(false);
    const [activeStoryId, setActiveStoryId] = React.useState(null);
    const [activeSegmentId, setActiveSegmentId] = React.useState(null);
    const [documentViewMode, setDocumentViewMode] = React.useState('text');
    const [selectedClusterIds, setSelectedClusterIds] = React.useState(() => new Set());
    const [selectedClaimKeys, setSelectedClaimKeys] = React.useState(() => new Set());
    const [creatingPitch, setCreatingPitch] = React.useState(false);
    const [createPitchError, setCreatePitchError] = React.useState(null);
    const [createPitchSuccessReportId, setCreatePitchSuccessReportId] = React.useState(null);
    const deltaBaselineDate = getFintelSearchParam(searchParams, 'BASELINE');

    const daysSinceBaseline = React.useMemo(() => getDaysSinceDate(baselineDate), [baselineDate]);

    React.useEffect(() => {
        if (getFintelSearchParam(searchParams, 'ENTITY')) {
            updateDeltaSearchParams({[FINTEL_SEARCH_PARAM_KEYS.ENTITY]: null});
        }
    }, [searchParams, updateDeltaSearchParams]);

    const openStoryInModal = React.useCallback((story, segmentId = null) => {
        if (!story?.clusterId) return;

        const safeSegments = Array.isArray(story.segments) ? story.segments : [];
        const selectedSegment = segmentId
            ? safeSegments.find((segment) => String(segment?.segmentId) === String(segmentId))
            : safeSegments[0];

        const selectedSegmentIndex = selectedSegment
            ? safeSegments.findIndex((segment) => segment?.segmentId === selectedSegment.segmentId)
            : null;

        setIsStoryModalOpen(true);
        setActiveStoryId(story.clusterId);
        setActiveSegmentId(selectedSegment?.segmentId ?? null);

        if (selectedSegment?.docId != null) {
            setDocument({
                documentId: String(selectedSegment.docId),
                docType: 'text',
                segmentIndex: selectedSegmentIndex,
                viewerContext: 'report',
            });
        }

        updateDeltaSearchParams({
            [FINTEL_SEARCH_PARAM_KEYS.MODAL_OPEN]: '1',
            [FINTEL_SEARCH_PARAM_KEYS.STORY]: story.clusterId,
            [FINTEL_SEARCH_PARAM_KEYS.SEGMENT]: selectedSegment?.segmentId ?? null,
        });
    }, [setDocument, updateDeltaSearchParams]);

    const handleClaimUpdateOpen = React.useCallback(
        (claim, update) => {
            const story = {
                clusterId: update.updateId,
                title:
                    update.summary?.trim() ||
                    update.sourceUpdate?.trim() ||
                    update.name?.trim() ||
                    'Update',
                description: update.sourceUpdate ?? '',
                tags: [],
                segments: Array.isArray(update.segments) ? update.segments : [],
                significance: update.significance,
                targetEntityLabels: Array.isArray(claim.targetEntityLabels) ? claim.targetEntityLabels : [],
                targetEntityIds: [],
                claimUpdates: [],
            };
            openStoryInModal(story);
        },
        [openStoryInModal]
    );

    const openRootInModal = React.useCallback(() => {
        setIsStoryModalOpen(true);
        setActiveStoryId(null);
        setActiveSegmentId(null);
        updateDeltaSearchParams({
            [FINTEL_SEARCH_PARAM_KEYS.MODAL_OPEN]: '1',
            [FINTEL_SEARCH_PARAM_KEYS.STORY]: null,
            [FINTEL_SEARCH_PARAM_KEYS.SEGMENT]: null,
        });
    }, [updateDeltaSearchParams]);

    const closeStoryModal = React.useCallback(() => {
        setIsStoryModalOpen(false);
        setActiveStoryId(null);
        setActiveSegmentId(null);
        updateDeltaSearchParams({
            [FINTEL_SEARCH_PARAM_KEYS.MODAL_OPEN]: null,
            [FINTEL_SEARCH_PARAM_KEYS.STORY]: null,
            [FINTEL_SEARCH_PARAM_KEYS.SEGMENT]: null,
        });
    }, [updateDeltaSearchParams]);

    const previousTabRef = React.useRef(activeTab);
    React.useEffect(() => {
        if (previousTabRef.current === activeTab) {
            return;
        }
        previousTabRef.current = activeTab;
        setIsStoryModalOpen(false);
        setActiveStoryId(null);
        setActiveSegmentId(null);
        updateDeltaSearchParams({
            [FINTEL_SEARCH_PARAM_KEYS.MODAL_OPEN]: null,
            [FINTEL_SEARCH_PARAM_KEYS.STORY]: null,
            [FINTEL_SEARCH_PARAM_KEYS.SEGMENT]: null,
        });
        setSelectedClusterIds(new Set());
        setSelectedClaimKeys(new Set());
        setCreatePitchError(null);
        setCreatePitchSuccessReportId(null);
    }, [activeTab, updateDeltaSearchParams]);

    React.useEffect(() => {
        if (storiesLoading || stories.length === 0) return;

        const shouldOpenModal = getFintelSearchParam(searchParams, 'MODAL_OPEN') === '1';
        const storyIdFromUrl = getFintelSearchParam(searchParams, 'STORY');

        if (!shouldOpenModal) return;
        if (!storyIdFromUrl) {
            openRootInModal();
            return;
        }

        const matchingStory = stories.find((story) => String(story?.clusterId) === String(storyIdFromUrl));
        if (!matchingStory) return;

        const segmentIdFromUrl = getFintelSearchParam(searchParams, 'SEGMENT');
        openStoryInModal(matchingStory, segmentIdFromUrl);
    }, [openRootInModal, openStoryInModal, searchParams, stories, storiesLoading]);

    React.useEffect(() => {
        setSelectedClusterIds(new Set());
        setSelectedClaimKeys(new Set());
        setCreatePitchError(null);
        setCreatePitchSuccessReportId(null);
    }, [baselineDate, deltaRunEpoch, activeTab]);

    React.useEffect(() => {
        if (isClaimsTab) {
            setSelectedClaimKeys((previous) => {
                if (!previous.size) return previous;
                const available = new Set(claimGroups.map((g) => String(g.claimKey)));
                const next = new Set([...previous].filter((id) => available.has(id)));
                return next.size === previous.size ? previous : next;
            });
            return;
        }
        setSelectedClusterIds((previous) => {
            if (!previous.size) return previous;
            const availableIds = new Set(stories.map((story) => String(story?.clusterId)));
            const next = new Set([...previous].filter((id) => availableIds.has(id)));
            return next.size === previous.size ? previous : next;
        });
    }, [stories, claimGroups, isClaimsTab]);

    const handleClusterToggle = React.useCallback((story, checked) => {
        const clusterId = story?.clusterId;
        if (clusterId == null) return;

        setCreatePitchError(null);
        setCreatePitchSuccessReportId(null);
        setSelectedClusterIds((previous) => {
            const next = new Set(previous);
            const key = String(clusterId);
            if (checked) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    }, []);

    const handleClaimToggle = React.useCallback((claim, checked) => {
        const key = claim?.claimKey;
        if (key == null || !String(key).trim()) return;
        setCreatePitchError(null);
        setCreatePitchSuccessReportId(null);
        setSelectedClaimKeys((previous) => {
            const next = new Set(previous);
            const k = String(key);
            if (checked) {
                next.add(k);
            } else {
                next.delete(k);
            }
            return next;
        });
    }, []);

    const handleCreatePitch = React.useCallback(async () => {
        const selectionEmpty = isClaimsTab ? selectedClaimKeys.size === 0 : selectedClusterIds.size === 0;
        if (creatingPitch || selectionEmpty) return;
        if (!user?.uid) {
            setCreatePitchError('You must be signed in to create a pitch.');
            return;
        }

        let docInitEntries;
        if (isClaimsTab) {
            if (selectedClaimKeys.size === 0) {
                setCreatePitchError('Please select at least one claim.');
                return;
            }
            docInitEntries = buildSourcePackageDocInitFromClaimGroups({
                claimGroups,
                selectedClaimKeys,
                userId: user.uid,
            });
        } else {
            const selectedStories = stories.filter((story) => selectedClusterIds.has(String(story?.clusterId)));
            if (!selectedStories.length) {
                setCreatePitchError('Please select at least one cluster.');
                return;
            }
            docInitEntries = buildSourcePackageDocInitFromStories({stories: selectedStories, userId: user.uid});
        }

        const baselineLabel = formatBaselineForTitle(deltaBaselineDate);
        const pitchKind = isTimelineTab ? 'Timeline' : 'Claims';
        const title = `Pitch - ${pitchKind} - ${baselineLabel}`;

        const payload = {
            title,
            topic: `${pitchKind} delta since ${baselineLabel}`,
            defaultQuery: '',
            productType: DEFAULT_PRODUCT_TYPE,
            userId: user.uid,
            userDisplayName: user.displayName || 'Anonymous',
            status: 'Draft',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            meta: {
                title,
                productType: DEFAULT_PRODUCT_TYPE,
                dtg: '',
                originator: {value: '', label: 'Select originator'},
                classification: {value: 'U', label: 'U - Unclassified'},
                distribution: [],
                compartment: [],
                confidence: {value: '', label: 'Select confidence'},
                ifc: {value: '', label: 'Select IFC'},
                productionCenter: {value: '', label: 'Select production center'},
            },
        };

        try {
            setCreatingPitch(true);
            setCreatePitchError(null);
            const {reportId} = await createReport(payload, docInitEntries);
            setCreatePitchSuccessReportId(reportId);
        } catch (error) {
            console.error('[DeltaPanel] Failed to create pitch from delta selection', error);
            setCreatePitchError('Failed to create pitch. Please try again.');
        } finally {
            setCreatingPitch(false);
        }
    }, [
        creatingPitch,
        deltaBaselineDate,
        activeTab,
        isTimelineTab,
        isClaimsTab,
        selectedClusterIds,
        selectedClaimKeys,
        claimGroups,
        stories,
        user,
    ]);

    const handleOpenCreatedPitch = React.useCallback(() => {
        if (!createPitchSuccessReportId) return;
        window.open(`/products/${createPitchSuccessReportId}/edit?aiSearch=`, '_blank', 'noopener,noreferrer');
    }, [createPitchSuccessReportId]);

    const handleCloseCreatePitchSuccess = React.useCallback(() => {
        setCreatePitchSuccessReportId(null);
        setCreatePitchError(null);
        setSelectedClusterIds(new Set());
        setSelectedClaimKeys(new Set());
    }, []);

    const activeStory = React.useMemo(
        () => stories.find((story) => String(story?.clusterId) === String(activeStoryId)) || null,
        [activeStoryId, stories]
    );
    const {activeSegmentIndexInDocument, showDocumentLoading, documentContentProps} = useDeltaDocumentContent({
        activeStory,
        activeSegmentId,
    });

    const modalTitle = isTimelineTab ? 'Timeline updates' : 'Claims delta';

    return (
        <React.Fragment>
            <Stack
                sx={{
                    width: FINTEL_SIDE_PANEL_WIDTH,
                    minWidth: FINTEL_SIDE_PANEL_WIDTH,
                    maxWidth: FINTEL_SIDE_PANEL_WIDTH,
                    borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                    bgcolor: 'background.paper',
                    height: 'calc(100vh - 64px)',
                    overflow: 'hidden',
                }}
            >
                <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                    <Tab value={DeltaPanelTab.CLAIMS} label="Delta · Claims"/>
                    <Tab value={DeltaPanelTab.TIMELINE} label="Delta · Timeline"/>
                    <Tab value={DeltaPanelTab.GAPS} label="Gaps"/>
                </Tabs>

                <Divider/>

                {isGapsTab ? (
                    <Box sx={{overflowY: 'auto', flex: 1}}>
                        <Stack spacing={0}>
                            <Box sx={{px: 2, pt: 2, pb: 1}}>
                                <Typography variant="subtitle1" fontWeight={700}>
                                    FINTEL gaps
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                                    Gaps extracted from the Gaps section and reformulated as EEI questions.
                                </Typography>
                            </Box>
                            <Box sx={{px: 2, pb: 2}}>
                                <GapsToEeiView
                                    reportId={reportIdForDelta}
                                    enabled={isGapsTab && !isProductLoading}
                                    focusGapId={focusGapId}
                                />
                            </Box>
                        </Stack>
                    </Box>
                ) : (
                    <Box sx={{overflowY: 'auto', flex: 1}}>
                        <DeltaClusterControls
                            isProductLoading={isProductLoading}
                            clustersLoading={storiesLoading}
                            baselineDate={baselineDate}
                            onBaselineDateChange={setBaselineDate}
                            daysSinceBaseline={daysSinceBaseline}
                            onRefreshDeltaRun={handleRefreshDeltaRun}
                            refreshDisabled={!reportIdForDelta || !String(reportIdForDelta).trim()}
                            heading={isClaimsTab ? 'FINTEL claims vs corpus' : 'Post-baseline timeline'}
                            description={
                                isClaimsTab
                                    ? 'Claims extracted from this report, with later documents that change how you should read them. Expand a claim to see updates; open an update for source text.'
                                    : 'Corpus updates from the baseline through today (UTC), contrasted with this FINTEL.'
                            }
                        />
                        {isTimelineTab && !isProductLoading && !hasPlainReportText && (
                            <Typography variant="body2" color="text.secondary" sx={{px: 2, pb: 2}}>
                                Report text is empty; cannot run the timeline delta.
                            </Typography>
                        )}
                        {isClaimsTab && !isProductLoading && !hasPlainReportText && (
                            <Typography variant="body2" color="text.secondary" sx={{px: 2, pb: 2}}>
                                Report text is empty; cannot extract claims for delta clustering.
                            </Typography>
                        )}
                        {isClaimsTab && (isProductLoading || hasPlainReportText) && (
                            <ClaimDeltasView
                                isProductLoading={isProductLoading}
                                storiesLoading={storiesLoading}
                                storiesError={storiesError}
                                claimDeltaStage={claimDeltaStage}
                                claimDeltaMessage={claimDeltaMessage}
                                claimUpdateProgressPercent={claimUpdateProgressPercent}
                                segmentClaimBatchIndex={segmentClaimBatchIndex}
                                segmentClaimBatchTotal={segmentClaimBatchTotal}
                                claimGroups={claimGroups}
                                selectedClaimKeys={selectedClaimKeys}
                                onClaimToggle={handleClaimToggle}
                                creatingPitch={creatingPitch}
                                onCreatePitch={handleCreatePitch}
                                createPitchError={createPitchError}
                                createPitchSuccess={Boolean(createPitchSuccessReportId)}
                                onOpenCreatedPitch={handleOpenCreatedPitch}
                                onCloseCreatePitchSuccess={handleCloseCreatePitchSuccess}
                                onUpdateOpen={handleClaimUpdateOpen}
                            />
                        )}
                        {isTimelineTab && (isProductLoading || hasPlainReportText) && (
                            <DeltaStoriesView
                                isProductLoading={isProductLoading}
                                deltaBaselineDate={deltaBaselineDate}
                                storiesLoading={storiesLoading}
                                storiesError={storiesError}
                                stories={stories}
                                selectedClusterIds={selectedClusterIds}
                                onClusterToggle={handleClusterToggle}
                                creatingPitch={creatingPitch}
                                onCreatePitch={handleCreatePitch}
                                createPitchError={createPitchError}
                                createPitchSuccess={Boolean(createPitchSuccessReportId)}
                                onOpenCreatedPitch={handleOpenCreatedPitch}
                                onCloseCreatePitchSuccess={handleCloseCreatePitchSuccess}
                                onStoryOpen={openStoryInModal}
                            />
                        )}
                    </Box>
                )}
            </Stack>

            <DeltaStoryModal
                open={isStoryModalOpen}
                onClose={closeStoryModal}
                selectedEntity={null}
                modalTitle={modalTitle}
                stories={stories}
                activeStory={activeStory}
                activeSegmentId={activeSegmentId}
                onOpenStory={openStoryInModal}
                onOpenRoot={openRootInModal}
                documentContentProps={documentContentProps}
                showDocumentLoading={showDocumentLoading}
                activeSegmentIndexInDocument={activeSegmentIndexInDocument}
                documentViewMode={documentViewMode}
                setDocumentViewMode={setDocumentViewMode}
            />
        </React.Fragment>
    );
};

export default DeltaPanel;

